import { ArrowLeft } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSWRConfig } from "swr"

import { AlfredClientConfigEditor } from "@/components/alfred/alfred-client-config-editor"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { Button } from "@/components/ui/button"
import { useCreateAlfredClient } from "@/hooks/use-alfred-clients"
import { useToast } from "@/hooks/use-toast"
import type { CreateAlfredClientRequest } from "@/types/alfred-client"
import { isAlfredClientListOrigin } from "@/lib/alfred-client-navigation"

export function AlfredClientCreatePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { mutate } = useSWRConfig()
  const createClient = useCreateAlfredClient()

  const returnToClients = () => {
    if (isAlfredClientListOrigin(location.state)) {
      navigate(-1)
      return
    }

    navigate("/alfred/clients")
  }

  const handleCreate = async (request: CreateAlfredClientRequest) => {
    try {
      await createClient.trigger(request)
      await mutate(["alfred", "clients"])
      toast({
        variant: "success",
        title: "Client created successfully",
        description: `Client ${request.clientId} has been added to the system.`,
      })
      returnToClients()
    } catch (error) {
      toast({
        variant: "error",
        title: "Failed to create client",
        description: error instanceof Error ? error.message : "Please try again.",
      })
    }
  }

  return (
    <>
      <DashboardHeader />
      <main className="container mx-auto px-4 py-6 space-y-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={returnToClients}
            className="text-muted-foreground hover:text-foreground hover:bg-secondary -ml-2 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Clients
          </Button>
          <h1 className="text-2xl font-semibold text-foreground">Add client</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure a new Alfred client for the system.
          </p>
        </div>

        <AlfredClientConfigEditor
          mode="create"
          onSubmit={handleCreate}
          isSubmitting={createClient.isMutating}
        />
      </main>
    </>
  )
}
